import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth, storage } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, limit, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import Toast from './Toast';
import ConfirmationModal from './modals/ConfirmationModalActions';


import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';

interface Country {
    id: string;
    name: string;
}

interface PersonalDetailsData {
    name: string;
    dob: string;
    gender: string;
    maritalStatus: string;
    nationality: string;
    state: string;
    district: string;
    // zipCode: string;
    pinCode: string;
    address: string;
    currentCountryOfResidence: string;
    number: string;
    alternateNumber1?: string;
    alternateNumber2?: string;
    email: string;
    whatsapp: string;
    photo?: string;
    hasPriorVisaRejection: boolean;
    priorVisaRejectionType?: string;
    priorVisaRejectionReason?: string;
    priorVisaRejectionCountryId?: string;
    passportNumber?: string;
}

interface CustomClaims {
    leadUid: string;
    subAgentId: string;
    companyId: string;
    role: string;
}

interface PersonalDetailsProps {
    write: boolean;
}

const PersonalDetails = ({ write }: PersonalDetailsProps) => {
    const [activeSection, setActiveSection] = useState<string>("Complete Profile");
    const [isLoading, setIsLoading] = useState(false);
    const [passportError, setPassportError] = useState(false);
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const countryDropdownRef = useRef<HTMLDivElement>(null);

    // Handle click outside country dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const { leadId: urlLeadId } = useParams();
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [companyId, setCompanyId] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [leadId, setLeadId] = useState(urlLeadId || "");
    
    // Phone validation state variables
    const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("");
    const [phoneValidationType, setPhoneValidationType] = useState<'error' | 'warning' | 'success' | ''>("");
    const [isPhoneValidating, setIsPhoneValidating] = useState<boolean>(false);
    
    const [formData, setFormData] = useState<PersonalDetailsData>({
        name: "",
        dob: "",
        gender: "",
        maritalStatus: "",
        nationality: "",
        state: "",
        district: "",
        currentCountryOfResidence: "",
        number: "",
        alternateNumber1: "",
        alternateNumber2: "",
        email: "",
        // zipCode: "",
        whatsapp: "",
        hasPriorVisaRejection: false,
        priorVisaRejectionType: "",
        priorVisaRejectionReason: "",
        priorVisaRejectionCountryId: "",
        pinCode: "",
        address: "",
    });

    const [photoName, setPhotoName] = useState<string>('No file selected');
    const [uploadTime, setUploadTime] = useState<string>('');
    const [photoUrl, setPhotoUrl] = useState<string>('');

    const genderOptions = ["Male", "Female", "Other"];
    const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed"];
    const visaTypeOptions = ["Student VISA", "Tourist VISA", "Work VISA"];

    // Country search and selection states
    const [countries, setCountries] = useState<Country[]>([]);
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [isLoadingCountry, setIsLoadingCountry] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);
    const dispatch = useAppDispatch();

    useEffect(() => {
        fetchData();
    }, [leadId]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            // Clear phone validation state when loading data
            setPhoneValidationMessage('');
            setPhoneValidationType('');
            setIsPhoneValidating(false);
            
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            // Get token claims
            const idTokenResult = await currentUser.getIdTokenResult();
            let companyId;

            // Check if we have student claims
            const claims = idTokenResult.claims as unknown as CustomClaims;
            if (claims.leadUid && claims.companyId) {
                // For student, use claims
                setLeadId(claims.leadUid);
                companyId = claims.companyId;
            } else if (claims.subAgentId && claims.companyId) {
                // For admin, use claims
                companyId = claims.companyId;
            } else {
                // For regular users, use URL param and user document
                if (!urlLeadId) return; // regular users must have leadId in URL
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }

            if (!companyId || !leadId) return;
            setCompanyId(companyId);

            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            if (companyDoc.exists()) {
                setCompanyName(companyDoc.data().name || "Unknown Company");
            }

            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const data = leadDoc.data() as PersonalDetailsData;
                // Convert Firestore Timestamp to local date string
                const dobDate = data.dob ? new Date(data.dob) : new Date();
                setFormData({
                    ...data,
                    dob: dobDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
                });
                if (data.photo) {
                    setPhotoName(data.photo.split('_').slice(1).join('_')); // Remove UUID prefix
                    fetchPhotoUrl(data.photo);
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPhotoUrl = async (photoName: string) => {
        try {
            if (!companyId || !leadId || !photoName) return;
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${photoName}`);
            const url = await getDownloadURL(storageRef);
            setPhotoUrl(url);
        } catch (error) {
            console.error("Error fetching photo URL:", error);
            setPhotoUrl('');
        }
    };

    useEffect(() => {
        if (formData.photo) {
            fetchPhotoUrl(formData.photo);
        } else {
            setPhotoUrl('');
        }
    }, [formData.photo, companyId, leadId]);

    // Check if phone number already exists in other leads (excluding current lead)
    const checkPhoneNumberExists = async (phoneNumber: string): Promise<{
        exists: boolean;
        leadData: any | null;
        assigneeName?: string;
    }> => {
        if (!companyId || !phoneNumber || !leadId) return { exists: false, leadData: null };

        try {
            // Clean and format the phone number for comparison
            let formattedNumber = phoneNumber.replace(/\D/g, '');
            if (!phoneNumber.startsWith('+') && formattedNumber.length > 0) {
                formattedNumber = "+" + formattedNumber;
            } else if (phoneNumber.startsWith('+')) {
                formattedNumber = phoneNumber;
            } else {
                formattedNumber = "+" + formattedNumber;
            }

            const leadsRef = collection(db, `companies/${companyId}/leads`);
            const q = query(leadsRef, where("number", "==", formattedNumber));
            const querySnapshot = await getDocs(q);

            // Exclude the current lead being edited
            const duplicateDocs = querySnapshot.docs.filter(doc => doc.id !== leadId);
            if (duplicateDocs.length > 0) {
                const docData = duplicateDocs[0].data();
                const leadData = { id: duplicateDocs[0].id, ...docData } as any;

                // Fetch assignee name
                let assigneeName = "Unknown";
                if (leadData.assignedTo) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            assigneeName = userData.name || "Unknown";
                        }
                    } catch (error) {
                        console.error("Error fetching assignee name:", error);
                    }
                }

                return {
                    exists: true,
                    leadData: leadData,
                    assigneeName: assigneeName
                };
            }

            return { exists: false, leadData: null };
        } catch (error) {
            console.error("Error checking phone number existence:", error);
            return { exists: false, leadData: null };
        }
    };

    // Real-time phone number validation function
    const validatePhoneRealTime = async (phoneNumber: string) => {
        setPhoneValidationMessage("");
        setPhoneValidationType("");

        if (!phoneNumber || phoneNumber.length < 1) {
            return;
        }

        setIsPhoneValidating(true);

        try {
            const result = await checkPhoneNumberExists(phoneNumber);

            if (result.exists && result.leadData) {
                const existingLeadName = result.leadData.name || "Unknown";
                const assigneeName = result.assigneeName || "Unknown";
                setPhoneValidationMessage(`Phone number already exists: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setPhoneValidationType("error");
            } else {
                setPhoneValidationMessage("Phone number is available");
                setPhoneValidationType("success");
            }
        } catch (error) {
            console.error("Error validating phone number:", error);
            setPhoneValidationMessage("Error validating phone number");
            setPhoneValidationType("error");
        } finally {
            setIsPhoneValidating(false);
        }
    };

    // Real-time phone number validation with debouncing
    useEffect(() => {
        if (!formData.number || formData.number.length < 1) {
            setPhoneValidationMessage("");
            setPhoneValidationType("");
            return;
        }

        const timeoutId = setTimeout(() => {
            validatePhoneRealTime(formData.number);
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [formData.number, companyId, leadId]);

    const handleInputChange = (field: keyof PersonalDetailsData, value: string) => {
        // For phone numbers, filter out alphabetic characters and spaces
        if (field === 'number' || field === 'alternateNumber1' || field === 'alternateNumber2') {
            // Allow only numbers, +, -, and parentheses (remove spaces and letters)
            value = value.replace(/[a-zA-Z\s]/g, '');
        }
        
        // Clear phone validation message when main phone number changes
        if (field === 'number') {
            setPhoneValidationMessage('');
            setPhoneValidationType('');
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const handlePhoneBlur = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        const fieldValue = formData[field];
        // Only set +91 for the main phone number field, not for alternate numbers
        if (field === "number" && (!fieldValue || fieldValue.trim() === "")) {
            setFormData(prev => ({ ...prev, [field]: "+91" }));
        }
        // For alternate numbers, clear the field if it's empty or only contains +91
        if ((field === "alternateNumber1" || field === "alternateNumber2") && 
            (!fieldValue || fieldValue.trim() === "" || fieldValue.trim() === "+91")) {
            setFormData(prev => ({ ...prev, [field]: "" }));
        }
    };
    const handlePhoneFocus = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        if (field === "number") {
            // Clear validation state when focusing on main phone number
            setPhoneValidationMessage('');
            setPhoneValidationType('');
        }

        const fieldValue = formData[field];
        if (!fieldValue || fieldValue.trim() === "") {
            setFormData(prev => ({ ...prev, [field]: "+91" }));
        }
    };

    const confirmSave = async () => {
        // First validate format
        if (formData.passportNumber && !/^[A-Za-z0-9]{8,12}$/.test(formData.passportNumber)) {
            setToastMessage('Invalid passport number format (8-12 alphanumeric characters)');
            setIsErrorToast(true);
            setShowToast(true);
            return;
        }

        // Check for duplicates only if passport number exists
        if (formData.passportNumber) {
            try {
                setIsLoading(true);
                const leadsRef = collection(db, `companies/${companyId}/leads`);
                const q = query(
                    leadsRef,
                    where('passportNumber', '==', formData.passportNumber),
                    where('__name__', '!=', leadId) // Exclude current lead
                );
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    setPassportError(true);
                    // setToastMessage('This passport number already exists');
                    setIsErrorToast(true);
                    // setShowToast(true);
                    setIsLoading(false);
                    return;
                }
            } catch (error) {
                console.error("Error checking passport number:", error);
                setToastMessage('Error checking passport number');
                setIsErrorToast(true);
                setShowToast(true);
                setIsLoading(false);
                return;
            } finally {
                setIsLoading(false);
            }
        }

        // If no duplicates, open confirmation modal
        setIsConfirmModalOpen(true);
    };


    const handleSave = async () => {
        setIsLoading(true);
        setPassportError(false); // Reset error state

        // Validate format first
        if (formData.passportNumber && !/^[A-Za-z0-9]{8,12}$/.test(formData.passportNumber)) {
            setIsLoading(false);
            return;
        }

        try {
            if (formData.passportNumber) {
                const leadsRef = collection(db, `companies/${companyId}/leads`);
                const q = query(
                    leadsRef,
                    where('passportNumber', '==', formData.passportNumber),
                    where('__name__', '!=', leadId)
                );
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    setPassportError(true);
                    setIsLoading(false);
                    return;
                }
            }

            const docRef = doc(db, `companies/${companyId}/leads/${leadId}`);

            // Helper function to convert empty strings to null
            const sanitizeData = (data: any) => {
                const sanitized: Record<string, any> = {};
                Object.keys(data).forEach(key => {
                    sanitized[key] = (typeof data[key] === 'string' && data[key].trim() === '')
                        ? null
                        : data[key];
                });
                return sanitized;
            };

            // Convert date to IST (UTC+5:30)
            const dobDate = formData.dob ? new Date(formData.dob) : null;
            const dobIST = dobDate ? new Date(dobDate.getTime() - (dobDate.getTimezoneOffset() * 60000)) : null;

            // Clean and format phone numbers
            const cleanedNumber = formData.number
                ? '+' + formData.number
                    .replace(/[a-zA-Z]/g, '')
                    .replace(/\s+/g, '')
                    .replace(/^\+/, '')
                : null;

            // Clean alternate numbers - only save if they contain actual numbers beyond +91
            const cleanAlternateNumber1 = formData.alternateNumber1?.trim();
            const cleanAlternateNumber2 = formData.alternateNumber2?.trim();
            
            const cleanedAlternateNumber1 = (cleanAlternateNumber1 && cleanAlternateNumber1 !== "+91" && cleanAlternateNumber1.length > 3)
                ? '+' + cleanAlternateNumber1
                    .replace(/[a-zA-Z]/g, '')
                    .replace(/\s+/g, '')
                    .replace(/^\+/, '')
                : null;

            const cleanedAlternateNumber2 = (cleanAlternateNumber2 && cleanAlternateNumber2 !== "+91" && cleanAlternateNumber2.length > 3)
                ? '+' + cleanAlternateNumber2
                    .replace(/[a-zA-Z]/g, '')
                    .replace(/\s+/g, '')
                    .replace(/^\+/, '')
                : null;

            const reversedNumber = cleanedNumber
                ? cleanedNumber.split('').reverse().join('')
                : null;

            const reversedAlternateNumber1 = cleanedAlternateNumber1
                ? cleanedAlternateNumber1.split('').reverse().join('')
                : null;

            const reversedAlternateNumber2 = cleanedAlternateNumber2
                ? cleanedAlternateNumber2.split('').reverse().join('')
                : null;

            // Prepare update data
            const updateData = {
                ...sanitizeData(formData),
                number: cleanedNumber,
                alternateNumber1: cleanedAlternateNumber1,
                alternateNumber2: cleanedAlternateNumber2,
                reversedNumber: reversedNumber,
                reversedAlternateNumber1: reversedAlternateNumber1,
                reversedAlternateNumber2: reversedAlternateNumber2,
                dob: dobIST?.toISOString() || null,
                updatedBy: auth.currentUser?.uid,
                updatedAt: serverTimestamp(),
            };

            await updateDoc(docRef, updateData);

            setToastMessage('Personal details saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            // Refresh the pipeline view if needed
            dispatch(setPipelineRefresh(true));
            console.log('Personal details saved successfully');
        } catch (error) {
            console.error("Error saving personal details:", error);
            setToastMessage('Error saving details. Please try again');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };

    // Handle country search
    const handleCountrySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const upperValue = value.toUpperCase();
        setCountrySearchTerm(value);

        if (value.length < 3) {
            setCountries([]);
            return;
        }

        setIsLoadingCountry(true);
        try {
            const countriesRef = collection(db, 'countries');
            const q = query(
                countriesRef,
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const countriesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setCountries(countriesData);
        } catch (error) {
            console.error("Error searching countries:", error);
            setToastMessage('Error searching countries');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoadingCountry(false);
        }
    };

    // Handle country selection
    const handleCountrySelect = (country: Country) => {
        setFormData(prev => ({
            ...prev,
            priorVisaRejectionCountryId: country.id
        }));
        setIsCountryDropdownOpen(false);
        setCountrySearchTerm(country.name);
        setCountries([]);
    };

    // Fetch country name when ID changes
    useEffect(() => {
        const fetchCountryName = async () => {
            if (formData.priorVisaRejectionCountryId) {
                try {
                    const countryDoc = await getDoc(doc(db, 'countries', formData.priorVisaRejectionCountryId));
                    if (countryDoc.exists()) {
                        setCountrySearchTerm(countryDoc.data().name);
                    }
                } catch (error) {
                    console.error('Error fetching country:', error);
                }
            }
        };
        fetchCountryName();
    }, [formData.priorVisaRejectionCountryId]);

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !companyId || !leadId) return;

        try {
            setIsLoading(true);

            // Validate file type
            if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                alert("Only JPG, JPEG, and PNG files are allowed");
                return;
            }

            // Delete existing photo if any
            if (formData.photo) {
                await handleDeletePhoto();
            }

            // Upload new photo
            const fileName = `${uuidv4()}_${file.name}`;
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${fileName}`);
            await uploadBytes(storageRef, file);

            // Update Firestore immediately
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(docRef, {
                photo: fileName
            });

            // Update state
            setPhotoName(file.name);
            setFormData(prev => ({
                ...prev,
                photo: fileName
            }));

            const now = new Date();
            const formattedDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
            const formattedTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            setUploadTime(`${formattedDate}, ${formattedTime} · Just now`);
        } catch (error) {
            console.error("Error uploading photo:", error);
            alert("Error uploading photo. Please try again.");
        } finally {
            setIsLoading(false);
            if (e.target) e.target.value = ''; // Reset file input
        }
    };



    const handleDeletePhoto = async () => {
        try {
            setIsLoading(true);
            if (!companyId || !leadId || !formData.photo) return;

            // Delete from storage
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${formData.photo}`);
            await deleteObject(storageRef);

            // Update Firestore
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(docRef, {
                photo: null
            });

            // Update state
            setPhotoName('No file selected');
            setUploadTime('');
            setPhotoUrl('');
            setFormData(prev => ({
                ...prev,
                photo: ''
            }));
            setToastMessage('Deleted Photo successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error deleting photo:", error);
            setToastMessage('Error deleting photo. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhotoClick = () => {
        try {
            if (photoUrl) {
                window.open(photoUrl, '_blank');
                setToastMessage('Photo opened successfully');
                setIsErrorToast(false);
                setShowToast(true);
            } else if (!isLoading) {
                fileInputRef.current?.click();
                setToastMessage('Select a photo to upload');
                setIsErrorToast(true);
                setShowToast(true);
            }
        } catch (error) {
            console.error('Error handling photo click:', error);
            setToastMessage('Error opening or selecting photo');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };


    const getInputContainerClass = (numColumns: number) => {
        switch (numColumns) {
            case 1:
                return "w-[30%] mt-5";
            case 2:
                return "grid grid-cols-2 gap-6 w-[60%] mt-5";
            case 3:
                return "grid grid-cols-3 gap-6 w-[90%] mt-5";
            default:
                return "w-3/4";
        }
    };

    const inputClass = `w-full py-3 px-4 bg-white border border-cyan-600 rounded-3xl focus:outline-none focus:ring-2 focus:ring-sky-950 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`;
    const labelClass = "block font-montserrat font-normal text-base text-black mb-2";

    return (
        <div>
            <div>
                {/* Photo Upload */}
                <div>
                    <label htmlFor="photo-upload" className="block font-montserrat font-normal text-base text-black mb-2">
                        Photo
                    </label>
                    <div className="relative w-3/4">
                        <div
                            onClick={handlePhotoClick}
                            className={`relative ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/${photoUrl ? 'teenyicons.svg' : 'catppuccin.svg'}`}
                                    alt="icon"
                                    className="w-8 h-8 text-gray-400"
                                />
                            </span>
                            <div className="absolute left-16 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                                {photoName}<br />
                                <span className="text-gray-500">{uploadTime}</span>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                id="photo-upload"
                                className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                onChange={handlePhotoChange}
                                accept=".jpg,.jpeg,.png"
                                disabled={isLoading || !write}
                                readOnly={!write}
                            />
                            <div className="w-full py-8 pl-14 pr-4 bg-white border border-cyan-600 rounded-xl hover:bg-gray-50 focus-within:ring-2 focus-within:ring-sky-950">
                                <span className="sr-only">Choose photo</span>
                            </div>
                        </div>
                        {formData.photo && (
                            <button
                                onClick={handleDeletePhoto}
                                disabled={isLoading}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Name, DOB */}
                <div className={getInputContainerClass(2)}>
                    <div>
                        <label htmlFor="name" className={labelClass}>Name</label>
                        <input
                            type="text"
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value.toUpperCase())}
                            placeholder="Full Name"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>

                    <div>
                        <label htmlFor="dob" className={labelClass}>Date of Birth</label>
                        <input
                            type="date"
                            id="dob"
                            value={formData.dob}
                            onChange={(e) => handleInputChange('dob', e.target.value)}
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>

                {/*Gender, Marital Status, Passport Number */}
                <div className={getInputContainerClass(3)}>

                    <div>
                        <label htmlFor="gender" className={labelClass}>Gender</label>
                        <select
                            id="gender"
                            value={formData.gender}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            className={inputClass}
                            disabled={!write}
                        >
                            <option value="">Select Gender</option>
                            {genderOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="marital-status" className={labelClass}>Marital Status</label>
                        <select
                            id="marital-status"
                            value={formData.maritalStatus}
                            onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                            className={inputClass}
                            disabled={!write}
                        >
                            <option value="">Select Marital Status</option>
                            {maritalStatusOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="passport-number" className={labelClass}>Passport Number</label>
                        <input
                            type="text"
                            id="passport-number"
                            value={formData.passportNumber || ''}
                            onChange={(e) => {
                                handleInputChange('passportNumber', e.target.value);
                                setPassportError(false); // Clear error when typing
                            }}
                            placeholder="Passport Number"
                            className={`${inputClass} ${passportError ? 'border-red-500' : ''}`} // Add red border if error
                            readOnly={!write}
                            disabled={!write}
                            pattern="[A-Za-z0-9]{8,12}"
                            title="Passport number should be 8-12 alphanumeric characters"
                        />
                        {/* Error message display */}
                        {passportError && (
                            <p className="mt-1 text-sm text-red-600">
                                This passport number already exists.
                            </p>
                        )}
                        {/* Format validation message */}
                        {formData.passportNumber && !/^[A-Za-z0-9]{8,12}$/.test(formData.passportNumber) && (
                            <p className="mt-1 text-sm text-yellow-600">
                                Passport number should be 8-12 alphanumeric characters
                            </p>
                        )}
                    </div>
                </div>

                {/* Nationality, State, District */}
                <div className={getInputContainerClass(3)}>
                    <div>
                        <label htmlFor="nationality" className={labelClass}>Nationality</label>
                        <input
                            type="text"
                            id="nationality"
                            value={formData.nationality}
                            onChange={(e) => handleInputChange('nationality', e.target.value)}
                            placeholder="Nationality"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                    <div>
                        <label htmlFor="state" className={labelClass}>State</label>
                        <input
                            type="text"
                            id="state"
                            value={formData.state}
                            onChange={(e) => handleInputChange('state', e.target.value)}
                            placeholder="State"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                    <div>
                        <label htmlFor="district" className={labelClass}>District</label>
                        <input
                            type="text"
                            id="district"
                            value={formData.district}
                            onChange={(e) => handleInputChange('district', e.target.value)}
                            placeholder="District"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>

                {/* Address */}
                <div className={getInputContainerClass(1)}>
                    <div>
                        <label htmlFor="address" className={labelClass}>Address</label>
                        <textarea
                            id="address"
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            placeholder="Enter full address"
                            className={`${inputClass} h-24 resize-none`}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>

                {/* Current Country of Residence */}
                <div className={getInputContainerClass(2)}>
                    <div>
                        <label htmlFor="current-country" className={labelClass}>Current Country of Residence</label>
                        <input
                            type="text"
                            id="current-country"
                            value={formData.currentCountryOfResidence}
                            onChange={(e) => handleInputChange('currentCountryOfResidence', e.target.value)}
                            placeholder="Country"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                    <div>
                        <label htmlFor="Pin code" className={labelClass}>Pin Code</label>
                        <input
                            type="text"
                            id="pin-code"
                            value={formData.pinCode}
                            onChange={(e) => handleInputChange('pinCode', e.target.value)}
                            placeholder="Pin Code"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>



                {/* Contact Information */}
                <div className={getInputContainerClass(3)}>
                    <div>
                        <label htmlFor="phone" className={labelClass}>Phone</label>
                        <input
                            type="tel"
                            id="phone"
                            value={formData.number}
                            onChange={(e) => handleInputChange('number', e.target.value)}
                            placeholder="Phone Number"
                            onFocus={() => handlePhoneFocus("number")}
                            onBlur={() => handlePhoneBlur("number")}
                            className={`${inputClass} ${
                                phoneValidationType === 'error' ? 'border-red-500' :
                                phoneValidationType === 'success' ? 'border-green-500' :
                                'border-cyan-600'
                            }`}
                            readOnly={!write}
                            disabled={!write}
                        />
                        {/* Phone validation message */}
                        {(phoneValidationMessage || isPhoneValidating) && write && (
                            <div className={`text-xs mt-1 ${
                                phoneValidationType === 'error' ? 'text-red-500' :
                                phoneValidationType === 'success' ? 'text-green-500' :
                                phoneValidationType === 'warning' ? 'text-yellow-500' :
                                'text-gray-500'
                            }`}>
                                {isPhoneValidating ? 'Validating...' : phoneValidationMessage}
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="alternateNumber1" className={labelClass}>Alternate Phone 1</label>
                        <input
                            type="tel"
                            id="alternateNumber1"
                            value={formData.alternateNumber1 || ''}
                            onChange={(e) => handleInputChange('alternateNumber1', e.target.value)}
                            placeholder="Alternate Phone Number 1"
                            onFocus={() => handlePhoneFocus("alternateNumber1")}
                            onBlur={() => handlePhoneBlur("alternateNumber1")}
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                    <div>
                        <label htmlFor="alternateNumber2" className={labelClass}>Alternate Phone 2</label>
                        <input
                            type="tel"
                            id="alternateNumber2"
                            value={formData.alternateNumber2 || ''}
                            onChange={(e) => handleInputChange('alternateNumber2', e.target.value)}
                            placeholder="Alternate Phone Number 2"
                            onFocus={() => handlePhoneFocus("alternateNumber2")}
                            onBlur={() => handlePhoneBlur("alternateNumber2")}
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>

                {/* Email and WhatsApp */}
                <div className={getInputContainerClass(2)}>
                    <div>
                        <label htmlFor="email" className={labelClass}>Email</label>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Email Address"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                    <div>
                        <label htmlFor="whatsapp" className={labelClass}>WhatsApp</label>
                        <input
                            type="tel"
                            id="whatsapp"
                            value={formData.whatsapp}
                            onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                            placeholder="WhatsApp Number"
                            className={inputClass}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>
                </div>

                {/* Visa Rejection Fields */}
                <div className={getInputContainerClass(1)}>
                    <div>
                        <label htmlFor="hasPriorVisaRejection" className={labelClass}>Any Prior VISA Rejections?</label>
                        <select
                            id="hasPriorVisaRejection"
                            value={(formData.hasPriorVisaRejection ?? false).toString()}
                            onChange={(e) => {
                                const value = e.target.value === 'true';
                                setFormData(prev => ({
                                    ...prev,
                                    hasPriorVisaRejection: value,
                                    // Reset related fields if No is selected
                                    ...((!value && {
                                        priorVisaRejectionType: "",
                                        priorVisaRejectionReason: "",
                                        priorVisaRejectionCountryId: ""
                                    }))
                                }));
                                // Clear country search when No is selected
                                if (!value) {
                                    setCountrySearchTerm("");
                                    setCountries([]);
                                }
                            }}
                            className={inputClass}
                            disabled={!write}
                        >
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </select>
                    </div>
                </div>

                {formData.hasPriorVisaRejection && (
                    <>
                        <div className={getInputContainerClass(1)}>
                            <div>
                                <label htmlFor="priorVisaRejectionType" className={labelClass}>Type of VISA</label>
                                <select
                                    id="priorVisaRejectionType"
                                    value={formData.priorVisaRejectionType}
                                    onChange={(e) => handleInputChange('priorVisaRejectionType', e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                >
                                    <option value="">Select VISA Type</option>
                                    {visaTypeOptions.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={getInputContainerClass(1)}>
                            <div>
                                <label htmlFor="priorVisaRejectionReason" className={labelClass}>Reason for Rejection</label>
                                <textarea
                                    id="priorVisaRejectionReason"
                                    value={formData.priorVisaRejectionReason}
                                    onChange={(e) => handleInputChange('priorVisaRejectionReason', e.target.value)}
                                    className={`${inputClass} h-24`}
                                    placeholder="Enter reason for visa rejection"
                                    readOnly={!write}
                                    disabled={!write}
                                />
                            </div>
                        </div>

                        <div className={getInputContainerClass(1)}>
                            <div className="relative">
                                <label htmlFor="priorVisaRejectionCountry" className={labelClass}>Rejected Country</label>
                                <input
                                    type="text"
                                    id="priorVisaRejectionCountry"
                                    value={countrySearchTerm}
                                    onChange={handleCountrySearchChange}
                                    onFocus={() => setIsCountryDropdownOpen(true)}
                                    placeholder="Search for a country"
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                />
                                {isCountryDropdownOpen && (
                                    <div ref={countryDropdownRef} className="absolute z-10 w-full mt-1 bg-white rounded-xl border border-cyan-600 shadow-lg">
                                        {isLoadingCountry ? (
                                            <div className="p-4 text-center text-gray-500">Loading...</div>
                                        ) : countries.length > 0 ? (
                                            <ul className="max-h-60 overflow-auto rounded-xl py-1 text-base">
                                                {countries.map((country) => (
                                                    <li
                                                        key={country.id}
                                                        onClick={() => handleCountrySelect(country)}
                                                        className="relative cursor-pointer select-none py-2 px-4 hover:bg-cyan-50"
                                                    >
                                                        {country.name}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : countrySearchTerm.length >= 3 ? (
                                            <div className="p-4 text-center text-gray-500">No countries found</div>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">Type at least 3 characters to search</div>
                                        )}
                                    </div>
                                )}

                            </div>
                        </div>
                    </>
                )}

                {/* Save Button */}
                {write && (
                    <div className="flex items-center justify-center h-full">
                        <button
                            onClick={confirmSave}
                            disabled={isLoading || phoneValidationType === 'error' || isPhoneValidating}
                            className={`text-center p-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] mt-5 mb-5 rounded-3xl font-medium hover:bg-gray-50 relative ${
                                isLoading || phoneValidationType === 'error' || isPhoneValidating 
                                    ? 'cursor-not-allowed opacity-75' 
                                    : ''
                            }`}
                        >
                            <span className={isLoading ? 'invisible' : ''}>Save & Proceed</span>
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                </div>
                            )}
                        </button>
                    </div>
                )}

                <ConfirmationModal
                    isOpen={isConfirmModalOpen}
                    isAdditional={isAdditionalActions}
                    onClose={() => setIsConfirmModalOpen(false)}
                    onSave={handleSave}
                    selectedStudent={{
                        id: leadId,
                        name: formData.name,
                        phone: formData.number,
                        passportNumber: formData.passportNumber
                    }}
                />

                {showToast && (
                    <Toast
                        message={toastMessage}
                        isError={toastMessage.includes('Error') || toastMessage.includes('Cannot') || toastMessage.includes('Failed')}
                        show={showToast}
                        onClose={() => setShowToast(false)}
                    />
                )}
                {/* {formData.passportNumber && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-800 font-medium">Passport Number: {formData.passportNumber}</p>
                        <p className="text-yellow-600 text-sm mt-1">
                            The system will check for duplicates before saving.
                        </p>
                    </div>
                )} */}
            </div>
        </div>
    );
};

export default PersonalDetails;
// Removed unused setPassportError function

